import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { httpsCallable } from 'firebase/functions'
import { X, AlertCircle, CheckCircle2 } from 'lucide-react'
import Logo from '@/components/shared/Logo'
import { functions } from '@/lib/firebase/config'
import { ASILIFLOW_SUPPORT_EMAIL, ASILIFLOW_SUPPORT_MAILTO } from '@/config/contact'

// Toast component
function Toast({ type, message, onClose }: { type: 'success' | 'error'; message: string; onClose: () => void }) {
  return (
    <div
      className={`fixed top-20 left-4 right-4 sm:left-auto sm:w-[360px] z-[60] rounded-lg shadow-lg px-4 py-3 text-sm ${
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 shrink-0">
          {type === 'success' ? <CheckCircle2 className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
        </span>
        <div className="flex-1">{message}</div>
        <button onClick={onClose} className="ml-2 opacity-80 hover:opacity-100">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
}

export default function Contact() {
  const location = useLocation()
  const intent = useMemo(() => {
    const value = new URLSearchParams(location.search).get('intent')
    return value === 'demo' ? 'demo' : 'support'
  }, [location.search])
  const pageCopy = intent === 'demo'
    ? {
        heading: 'Book a Demo',
        subheading: 'Tell us about your agency and we will follow up to schedule a walkthrough.',
        formTitle: 'Request a Demo',
        messageLabel: 'What should we cover? *',
        messagePlaceholder: 'Share your agency size, current workflow, and what you want to see in the demo.',
        submitLabel: 'Send Demo Request',
        successMessage: 'Demo request sent. We will follow up shortly.',
      }
    : {
        heading: 'Get in Touch',
        subheading: "Have questions? We'd love to hear from you.",
        formTitle: 'Send us a Message',
        messageLabel: 'Message *',
        messagePlaceholder: 'How can we help you?',
        submitLabel: 'Send Message',
        successMessage: "Message sent successfully. We'll get back to you shortly.",
      }
  const [formData, setFormData] = useState({ name: '', email: '', company: '', message: '' })
  const [submitting, setSubmitting] = useState(false)
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)

    try {
      const submitPublicContact = httpsCallable(functions, 'submitPublicContact')
      await submitPublicContact({ ...formData, intent })
      setToast({ type: 'success', message: pageCopy.successMessage })
      setFormData({ name: '', email: '', company: '', message: '' })
    } catch {
      setToast({ type: 'error', message: 'Something went wrong. Please email ' + ASILIFLOW_SUPPORT_EMAIL + '.' })
    } finally {
      setSubmitting(false)
      setTimeout(() => setToast(null), 3500)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-100 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <Logo size="lg" />
            </Link>
            <nav className="hidden sm:block">
              <ol className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                <li>
                  <Link to="/" className="hover:text-gray-700 dark:hover:text-gray-300">
                    Home
                  </Link>
                </li>
                <li aria-hidden>•</li>
                <li className="text-gray-700 dark:text-gray-300 font-medium">Contact Us</li>
              </ol>
            </nav>
          </div>
        </div>
      </header>

      {/* Main */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 pt-24">
        <div className="max-w-2xl mx-auto text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mb-4">{pageCopy.heading}</h1>
          <p className="text-gray-700 dark:text-gray-300 text-lg">{pageCopy.subheading}</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm p-6 sm:p-8 max-w-2xl mx-auto"
        >
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">{pageCopy.formTitle}</h2>

          <div className="mb-4">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Name *
            </label>
            <input
              type="text"
              id="name"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
              placeholder="Your full name"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              id="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
              placeholder="you@youragency.com"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="company" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Company / Agency
            </label>
            <input
              type="text"
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition"
              placeholder="Your agency name"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {pageCopy.messageLabel}
            </label>
            <textarea
              id="message"
              required
              rows={5}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition resize-none"
              placeholder={pageCopy.messagePlaceholder}
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className={`w-full text-white py-3 rounded-lg font-semibold focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition ${
              submitting ? 'bg-primary-300 cursor-not-allowed' : 'bg-primary-600 hover:bg-primary-700'
            }`}
          >
            {submitting ? 'Sending...' : pageCopy.submitLabel}
          </button>
        </form>

        {/* Contact Info */}
        <div className="max-w-2xl mx-auto mt-12 text-center">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Other Ways to Reach Us</h3>
          <div className="space-y-2 text-gray-600 dark:text-gray-400">
            <p>
              Email:{' '}
              <a href={ASILIFLOW_SUPPORT_MAILTO} className="text-primary-600 hover:underline">
                {ASILIFLOW_SUPPORT_EMAIL}
              </a>
            </p>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="mt-16 border-t border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 text-sm text-gray-500 dark:text-gray-400 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} AsiliFlow, a product of Asili LLC. All rights reserved.</p>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-gray-700 dark:hover:text-gray-300">
              Terms of Service
            </Link>
            <Link to="/privacy" className="hover:text-gray-700 dark:hover:text-gray-300">
              Privacy Policy
            </Link>
          </div>
        </div>
      </footer>
    </div>
  )
}
